import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { CustomerModel } from '@df/session/api/customer.model';
import { DragScrollDirective } from '@df/ui/common/drag-scroll.directive';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { TradePricingMaskDirective } from '../../../checkout/module/trade/pricing/trade-pricing-mask.directive';
import { LocalProductConfigurationBaseService } from '../configuration/local-product-configuration-base.service';
import { Product } from '../product.decorator';

export interface IProductTierPrice {
    qty: number;
    sqmQty: number;
    price: number;
    savePercentage?: number;
}

@Component({
    selector: 'product-tier-price',
    templateUrl: './product-tier-price.html',
    styleUrls: ['./product-tier-price.scss'],
    standalone: true,
    imports: [PriceComponent, CommonModule, TranslationModule, DragScrollDirective, TradePricingMaskDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductTierPriceComponent extends UiComponent implements OnDestroy, OnInit {
    protected tierPrices?: IProductTierPrice[];

    /**
     * In no-configuration mode that is an alternative source of product data
     */
    @Input('product')
    _product?: Product;

    @Output()
    qtyChange = new EventEmitter<number>();

    /**
     * Configuration service
     */
    private readonly productConfiguration = inject(ProductConfiguration, { optional: true });
    private readonly customerModel = inject(CustomerModel);

    protected get displayDragScroll(): boolean {
        return !!(this.client.isS && this.tierPrices && this.tierPrices.length > 2);
    }

    protected get product(): Product {
        if (this._product) {
            return this._product;
        }
        return this.productConfiguration!.product$.value as Product;
    }

    protected get isSqM(): boolean {
        return this.baseService.isSqM;
    }

    private get baseService(): LocalProductConfigurationBaseService {
        return this.productConfiguration!.baseService as LocalProductConfigurationBaseService;
    }

    override ngOnInit(): void {
        const update = this.timeoutService.debounce(() => this.updateTierPrices(), 100);

        this.subscriptions.add(this.customerModel.obs.data.subscribe(update));
        if (this.productConfiguration) {
            this.subscriptions.add(this.productConfiguration.priceChange$.subscribe(update));
            this.subscriptions.add(this.baseService.change$.subscribe(update));
        }
        update();

        super.ngOnInit();
    }

    protected selectPrice(price: IProductTierPrice): void {
        if (this.productConfiguration) {
            this.baseService.displayQty = this.isSqM ? price.sqmQty : price.qty;
        }
        this.qtyChange.emit(price.qty);
    }

    /**
     * Calculate percentage saved compared to base price
     */
    private calculateSavePercentage(tierPrice: number): number {
        const basePrice = this.product.price;
        return Math.round(((basePrice - tierPrice) / basePrice) * 100);
    }

    private updateTierPrices(): void {
        const tierPrices = this.productConfiguration
            ? this.baseService.tierPrices
            : this.product.getTierPrices(this.customerModel.data.group_id);

        this.tierPrices = tierPrices
            ?.filter(i => i.qty > 1)
            .map(i => ({
                qty: i.qty,
                price: i.price,
                sqmPrice: this.productConfiguration ? this.baseService.toSqMPrice(i.price) : undefined,
                sqmQty: this.productConfiguration ? this.baseService.qtyToSqM(i.qty) : undefined,
                savePercentage: this.calculateSavePercentage(i.price)
            }));

        this.detectChanges();
    }
}
